import { Component, forwardRef, Input, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { combineLatest, EMPTY, of } from 'rxjs';
import { map, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  host: { 'class': 'fw-time-field' },
  selector: 'fw-time-field',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FwTimeFieldComponent), // eslint-disable-line @typescript-eslint/no-use-before-define
    multi: true,
  }],
  template: `
    <input type="number" [formControl]="hours" (blur)="onTouched()" [min]="minHours" [max]="maxHours">
    <span>:</span>
    <input type="number" [formControl]="minutes" (blur)="onTouched()" min="0" max="59">
  `,
  styleUrls: ['./fw-time-field.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FwTimeFieldComponent implements ControlValueAccessor {
  @Input() meridiem = false

  hours = new FormControl()
  minutes = new FormControl()

  onChange: (value: string) => void = () => {} // eslint-disable-line @typescript-eslint/no-empty-function
  onTouched: Function = () => {} // eslint-disable-line @typescript-eslint/no-empty-function

  get minHours(): number {
    return this.meridiem ? 1 : 0;
  }

  get maxHours(): number {
    return this.meridiem ? 12 : 23;
  }

  constructor() {
    combineLatest([
      this.hours.valueChanges.pipe(
        switchMap(hours => {
          const scoped = Math.max(this.minHours, Math.min(parseInt(String(hours).slice(-2)), this.maxHours));
          const padded = this.meridiem ? String(scoped) : String(scoped).padStart(2, '0');
          if (hours !== padded) {
            this.hours.setValue(padded);
            return EMPTY;
          }
          return of(padded);
        })
      ),
      this.minutes.valueChanges.pipe(
        switchMap(minutes => {
          const scoped = Math.max(0, Math.min(parseInt(String(minutes).slice(-2)), 59));
          const padded = String(scoped).padStart(2, '0');
          if (minutes !== padded) {
            this.minutes.setValue(padded);
            return EMPTY;
          }
          return of(padded);
        })
      ),
    ]).pipe(
      map(([hours, minutes]) => `${hours}:${minutes}`),
      distinctUntilChanged(),
    ).subscribe(time => {
      this.onChange(time);
    });
  }

  writeValue(time: string): void {
    const [hours, minutes] = (time || '').split(':');
    this.hours.setValue(hours);
    this.minutes.setValue(minutes);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    const method = isDisabled ? 'disable' : 'enable';
    this.hours[method]();
    this.minutes[method]();
  }
}
